import React from "react";
import {
    Document,
    Page,
    Text,
    View,
    StyleSheet,
    Image,
    Font,
} from "@react-pdf/renderer";
import fontRegular from './fonts/NotoSansTamil-Regular.ttf';
import fontBold from "./fonts/NotoSansTamil-Bold.ttf";
import dayjs from "dayjs";

// Register Tamil Unicode Fonts
Font.register({
    family: "fontRegular",
    src: fontRegular,
});
Font.register({
    family: "fontBold",
    src: fontBold,
});

// Styles
const styles = StyleSheet.create({
    page: {
        padding: 30,
        fontSize: 11,
        fontFamily: "fontRegular",
        backgroundColor: '#ffffff'
    },
    pageBorder: {
        border: '1pt solid #000',
        padding: 25,
        height: '100%',
        flexDirection: 'column'
    },
    header: {
        alignItems: "center",
        textAlign: "center",
        marginBottom: 15,
        borderBottom: "1.5pt solid #000",
        paddingBottom: 15,
    },
    companyName: {
        fontFamily: 'fontBold',
        fontSize: 22,
        textAlign: 'center',
        marginBottom: 8,
    },
    address: {
        fontSize: 10,
        color: '#333',
        textAlign: 'center',
        marginBottom: 2,
    },
    noticeTitleView: {
        alignItems: "center",
        justifyContent: "center",
        flexDirection: "column",
        marginVertical: 10,
        backgroundColor: '#f0f0f0',
        paddingVertical: 10,
        paddingHorizontal: 20,
        borderRadius: 4,
    },
    noticeTitle: {
        fontSize: 14,
        fontFamily: "fontBold",
    },
    subTitle: {
        fontSize: 10,
        fontFamily: "fontBold",
        marginTop: 6
    },
    refDateRow: {
        flexDirection: "row",
        justifyContent: "space-between",
        marginBottom: 20,
        fontFamily: "fontBold",
        fontSize: 11
    },
    receiverBlock: {
        marginBottom: 20,
        fontSize: 11,
        lineHeight: 1.6,
        fontFamily: "fontBold",
    },
    subjectBlock: {
        marginBottom: 20,
        paddingHorizontal: 10
    },
    subjectText: {
        fontSize: 11,
        fontFamily: "fontBold",
        lineHeight: 1.6,
        textAlign: "left"
    },
    salutation: {
        fontSize: 11,
        fontFamily: "fontBold",
        marginBottom: 10,
    },
    table: {
        border: "1pt solid #000",
        marginBottom: 10,
        marginTop: 25,
        width: '100%'
    },
    tableHeader: {
        flexDirection: "row",
        borderBottom: "1pt solid #000",
        backgroundColor: '#e6e6e6'
    },
    tableCell: {
        flex: 1,
        borderRight: "1pt solid #000",
        padding: 6,
        fontSize: 9,
        textAlign: "center",
        fontFamily: "fontBold",
    },
    tableCellDetail: {
        flex: 1.2,
        borderRight: "1pt solid #000",
        padding: 6,
        fontSize: 9,
        textAlign: "center",
        fontFamily: "fontBold",
    },
    tableCellLast: {
        flex: 1.5,
        padding: 6,
        fontSize: 9,
        textAlign: "center",
        fontFamily: "fontBold",
    },
    tableRow: {
        flexDirection: "row",
        borderBottom: "1pt solid #000",
    },
    tableCellData: {
        flex: 1,
        borderRight: "1pt solid #000",
        padding: 6,
        fontSize: 9,
        textAlign: "center",
    },
    tableCellDataDetail: {
        flex: 1.2,
        borderRight: "1pt solid #000",
        padding: 6,
        fontSize: 9,
        textAlign: "center",
    },
    tableCellDataLast: {
        flex: 1.5,
        padding: 6,
        fontSize: 9,
        textAlign: "center",
    },
    paragraph: {
        fontSize: 11,
        lineHeight: 1.6,
        textAlign: "left",
        marginBottom: 15,
    },
    footerContainer: {
        marginTop: 20,
        flexDirection: 'row',
        justifyContent: 'flex-end',
    },
    signatureBlock: {
        width: 220,
        alignItems: 'center'
    },
    footerText: {
        fontSize: 11,
        fontFamily: "fontBold",
        marginBottom: 5,
        textAlign: "center",
    }
});

const MonthElevenNoticePDF = ({ date, customerName, address, loanNumber, pawnDate, loanAmount, interest, fatherName, mobileNumber, totalWeight, jewelDetails, agreedPeriod }) => {
    const loanDate = dayjs(pawnDate, ["YYYY-MM-DD", "DD-MM-YYYY"]);
    const pawnDateFormatted = loanDate.isValid() ? loanDate.format("DD/MM/YYYY") : pawnDate;
    const todayFormatted = dayjs().format("DD/MM/YYYY");

    // Calculate maturity date dynamically based on agreedPeriod
    let maturityDate = pawnDate;
    if (loanDate.isValid()) {
        let addValue = 12;
        let addUnit = 'month';
        if (agreedPeriod) {
            const str = agreedPeriod.toString().toLowerCase();
            const num = parseInt(str) || 1;
            addValue = num;
            if (str.includes("year")) addUnit = 'year';
            else if (str.includes("month")) addUnit = 'month';
            else if (str.includes("day")) addUnit = 'day';
        }
        maturityDate = loanDate.add(addValue, addUnit).format("DD/MM/YYYY");
    }

    return (
        <Document>
            <Page size="A4" style={styles.page}>
                <View style={styles.pageBorder}>

                    {/* Header */}
                    <View style={styles.header}>
                        <Text style={styles.companyName}>
                            ஸ்ரீ விக்னேஷ்வரா பைனான்ஸ்
                        </Text>
                        <Text style={styles.address}>
                            1/52/1, விநாயகர் கோவில் தெரு, கப்பலூர், திருமங்கலம் TK.
                        </Text>
                    </View>

                    {/* Ref & Date */}
                    <View style={styles.refDateRow}>
                        <Text>கடன் எண் : {loanNumber}</Text>
                        <Text>தேதி : {date}</Text>
                    </View>

                    {/* Center Title */}
                    <View style={styles.noticeTitleView}>
                        <Text style={styles.noticeTitle}>நினைவூட்டல் அறிவிப்பு</Text>
                        <Text style={styles.noticeTitle2}>{" "}</Text>
                        <Text style={styles.subTitle}>(பதிவஞ்சலுடன் கூடியது)</Text>
                    </View>

                    {/* Receiver Info */}
                    <View style={styles.receiverBlock}>
                        <Text>பெறுநர் :</Text>
                        <Text>திரு/திருமதி {customerName}</Text>
                        {fatherName && <Text>C/O : {fatherName}</Text>}
                        <Text>{address}</Text>
                        {mobileNumber && <Text>Ph : {mobileNumber}</Text>}
                    </View>

                    {/* Subject Block */}
                    <View style={styles.subjectBlock}>
                        <Text style={styles.subjectText}>
                            பொருள் : தங்களது தங்கநகை கடன்களுக்கான வட்டி நிலுவைத்தொகையை {" "} செலுத்த வேண்டிய நினைவூட்டல் அறிவிப்பு.
                        </Text>
                    </View>

                    {/* Salutation */}
                    <Text style={styles.salutation}>அன்புடையீர் வணக்கம்,</Text>

                    {/* Paragraphs */}
                    <Text style={styles.paragraph}>
                        தாங்கள் கடந்த {pawnDateFormatted} ம் தேதி {totalWeight} கிராம் எடையுள்ள தங்க ஆபரணங்களை வைத்துக்கொண்டு ரூபாய் {loanAmount} க்கான கடனுக்காக எமது நிறுவனத்தை அணுகியுள்ள்ளீர்கள். அதன்படி தாங்கள் எமது நிறுவனத்தின் விதிமுறைகள் மற்றும் நிபந்தனைகளுக்கு ஒப்புக்கொண்டதின் பேரில், நாங்கள் {" "}தொடர்புடைய கடன் ஆவணங்களை செயற்படுத்தி {loanNumber} என்ற கடன் கணக்கில் மேற்படிகடன்தொகையானது தங்களுக்கு வழங்கப்பட்டுள்ளது. {" "}
                    </Text>
                    <Text>{"  "}</Text>
                    <Text>{"  "}</Text>
                    <Text style={styles.paragraph}>
                        {" "}எங்களது நிதி நிறுவனமான ஸ்ரீ விக்னேஷ்வரா பைனான்ஸ் மீது, தாங்கள் வைத்திருக்கும் தங்கநகை கடன்களுக்கான நம்பிக்கைக்காக உங்களை பாராட்டுகிறோம். இருப்பினும் கடந்த {todayFormatted} ம் தேதியன்று தாங்கள் செலுத்த வேண்டிய வட்டித்தொகை இன்னும் செலுத்தப்படாமல் உள்ளது என்பதினை குறித்து எங்களது கவனத்திற்கு வந்துள்ளது. இந்நிலையில் நீங்கள் சரியான நேரத்தில் வட்டித்தொகை செலுத்துவது என்பது ஒரு சுமூகமான நிதிஉறவை {" "}பேணுவதற்குரிய முக்கிய செயலாகும்.
                    </Text>

                    {/* Table */}
                    <View style={styles.table}>
                        <View style={styles.tableHeader}>
                            <Text style={styles.tableCell}>கடன் எண்</Text>
                            <Text style={styles.tableCell}>கடன் தேதி</Text>
                            <Text style={styles.tableCellDetail}>கடன் தொகை</Text>
                            <Text style={styles.tableCell}>முடிவு தேதி</Text>
                            <Text style={styles.tableCellLast}>நகை விவரம்</Text>
                        </View>
                        <View style={[styles.tableRow, { borderBottom: "none" }]}>
                            <Text style={styles.tableCellData}>{loanNumber}</Text>
                            <Text style={styles.tableCellData}>{pawnDateFormatted}</Text>
                            <Text style={styles.tableCellDataDetail}>ரூ. {loanAmount}</Text>
                            <Text style={styles.tableCellData}>{maturityDate}</Text>
                            <Text style={styles.tableCellDataLast}>{jewelDetails}</Text>
                        </View>
                    </View>

                    <Text style={styles.paragraph}>
                        ஆகையால், தங்களது கடன்தொகைக்குரிய வட்டி நிலுவைத்தொகையான {" "}ரூபாய் {interest} ஐ எதிர்வரும் 7 நாட்களுக்குள் செலுத்துவதற்கான நினைவூட்டலாக இந்த அறிவிப்பை எடுத்துக்கொள்ளுங்கள். இதன் மூலம் கூடுதல் கட்டணங்களை விதிப்பதை தவிர்க்கவும் அடகு வைக்கப்பட்ட தங்கத்தின் பாதுகாப்பை உறுதி செய்து கொள்ளவும் {" "}என்பதினை இவ்வறிவிப்பு மூலம் தங்களுக்கு தெரிவித்துக்கொள்ளப்படுகிறது {" "}.
                    </Text>

                    {/* Footer / Signature Box */}
                    <View style={styles.footerContainer}>
                        <View style={styles.signatureBlock}>
                            <Text style={styles.footerText}>தங்கள் உண்மையுள்ள,</Text>
                            <Text style={[styles.footerText, { marginVertical: 10 }]}></Text>
                            <Text style={styles.footerText}>கிளைமேலாளர்,</Text>
                            <Text style={styles.footerText}>ஸ்ரீ விக்னேஷ்வரா பைனான்ஸ்,</Text>
                            <Text style={styles.footerText}>கப்பலூர், திருமங்கலம் TK.</Text>
                        </View>
                    </View>

                </View>
            </Page>
        </Document>
    );
};

export default MonthElevenNoticePDF;